/**
 * What a diagram IS, on this canvas.
 *
 * The field names deliberately mirror Lucid's own — fill, stroke,
 * strokeWidth, strokeStyle, rotation, z, elbow connectors with a
 * rounding, endpoint caps of none/arrow — because the charts this
 * replaces were drawn in Lucid and are still being drawn there. Matching
 * the vocabulary is what makes `fromLucid` a translation rather than an
 * interpretation, and a chain of four hundred parties is not something
 * anybody is going to redraw by hand to change tools.
 *
 * A shape's TEXT is one string with newlines, not rich runs. Every box
 * in the real charts is a name, a date and a recording reference stacked
 * on separate lines in one weight — "WD 372/282 / 12/16/1977 / ARTI /
 * SE, S2NE of Sec. 16" — so per-character styling would be a large
 * amount of machinery serving nothing anybody draws.
 */

let counter = 0;
/** Ids are per-document and short; collisions are prevented by the seed. */
export function newId(prefix = 's') {
  counter += 1;
  return `${prefix}${Date.now().toString(36)}${counter.toString(36)}`;
}

export const DEFAULTS = Object.freeze({
  // 160×160 is what the real charts use for a party, and a new shape
  // that matches the ones already on the page is one less thing to
  // resize.
  w: 160,
  h: 160,
  fill: '#ffffff',
  stroke: '#3a414a',
  strokeWidth: 2,
  strokeStyle: 'solid',
  textColor: '#000000',
  // A chain of title is read at arm's length on a laptop and printed
  // for a client, and 10pt was set for neither — it was set for fitting
  // as much as possible into a box. 13 is the size the rest of this app
  // uses for text somebody is expected to read.
  fontSize: 13,
  fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
  align: 'center',
  valign: 'middle',
  rotation: 0,
});

export function makeShape(patch = {}) {
  return {
    id: patch.id || newId('s'),
    kind: 'shape',
    shapeType: patch.shapeType || 'rectangle',
    x: patch.x ?? 0,
    y: patch.y ?? 0,
    w: patch.w ?? DEFAULTS.w,
    h: patch.h ?? DEFAULTS.h,
    rotation: patch.rotation ?? 0,
    fill: patch.fill ?? DEFAULTS.fill,
    stroke: patch.stroke ?? DEFAULTS.stroke,
    strokeWidth: patch.strokeWidth ?? DEFAULTS.strokeWidth,
    strokeStyle: patch.strokeStyle ?? DEFAULTS.strokeStyle,
    text: patch.text ?? '',
    textColor: patch.textColor ?? DEFAULTS.textColor,
    fontSize: patch.fontSize ?? DEFAULTS.fontSize,
    bold: patch.bold ?? false,
    italic: patch.italic ?? false,
    underline: patch.underline ?? false,
    align: patch.align ?? DEFAULTS.align,
    valign: patch.valign ?? DEFAULTS.valign,
    z: patch.z ?? 0,
    groupId: patch.groupId ?? null,
    // Where this box came from, when it came from the runsheet rather
    // than somebody's hand. Carried so a redraw can find it again —
    // and `sourceKind` alongside the id because a party is keyed by its
    // match key and a conveyance by its runsheet row, which can collide
    // across the two. Without it a rebuild recognises neither and draws
    // the whole chain a second time.
    origin: patch.origin ?? 'manual',
    sourceId: patch.sourceId ?? null,
    sourceKind: patch.sourceKind ?? null,
    // The deed behind an instrument box, so clicking it can open the PDF.
    documentId: patch.documentId ?? null,
  };
}

export function makeEdge(patch = {}) {
  return {
    id: patch.id || newId('e'),
    kind: 'edge',
    // An endpoint is either attached to a shape or pinned to a point.
    // Attached is the default and the reason connectors survive a drag:
    // the line is re-solved against the shape's current rect every
    // render rather than remembered as coordinates.
    from: patch.from ?? null,
    to: patch.to ?? null,
    lineShape: patch.lineShape ?? 'elbow',
    rounding: patch.rounding ?? 12,
    stroke: patch.stroke ?? DEFAULTS.stroke,
    strokeWidth: patch.strokeWidth ?? DEFAULTS.strokeWidth,
    strokeStyle: patch.strokeStyle ?? DEFAULTS.strokeStyle,
    startCap: patch.startCap ?? 'none',
    endCap: patch.endCap ?? 'arrow',
    // Points a person dragged the line through. Empty means routed.
    waypoints: patch.waypoints ?? [],
    label: patch.label ?? '',
    // How far along the line the label sits, 0 at the tail and 1 at the
    // head. A fraction rather than a point, so it stays where it was put
    // relative to the line when the shapes either end of it move.
    labelPos: patch.labelPos ?? 0.5,
    labelColor: patch.labelColor ?? DEFAULTS.textColor,
    fontSize: patch.fontSize ?? 11,
    bold: patch.bold ?? true,
    // Alongside bold, so the type shortcuts mean the same thing on a
    // conveyance as on a party. Without these ⌘I on a selected line
    // wrote a field nothing read — a shortcut that appeared to work.
    italic: patch.italic ?? false,
    underline: patch.underline ?? false,
    z: patch.z ?? 0,
    groupId: patch.groupId ?? null,
    origin: patch.origin ?? 'manual',
    sourceId: patch.sourceId ?? null,
    sourceKind: patch.sourceKind ?? null,
  };
}

/* ------------------------------------------------------------ Lucid -- */

/** "#3a414aff" → "#3a414a". Lucid writes RGBA; CSS is happier without. */
export function fromLucidColor(value, fallback = '#000000') {
  const text = String(value || '').trim();
  const m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(text);
  if (!m) return fallback;
  // A fully transparent fill is "no fill", which is a real choice in
  // these charts — a text block over a connector.
  if (m[2] && m[2].toLowerCase() === '00') return 'transparent';
  return `#${m[1].toLowerCase()}`;
}

/** "x: 12.5, y: -3, w: 160, h: 160" → numbers. */
export function parseBoundingBox(text) {
  const out = { x: 0, y: 0, w: DEFAULTS.w, h: DEFAULTS.h };
  for (const part of String(text || '').split(',')) {
    const m = /^\s*([xywh])\s*:\s*(-?[\d.]+)/.exec(part);
    if (m) out[m[1]] = Number(m[2]);
  }
  return out;
}

/** Lucid's block classes, mapped onto the shapes this canvas draws. */
const BLOCK_SHAPE = {
  DefaultSquareBlock: 'rectangle',
  RoundedRectangleBlock: 'rounded',
  CircleBlock: 'ellipse',
  EllipseBlock: 'ellipse',
  DiamondBlock: 'diamond',
  OctagonBlock: 'octagon',
  HexagonBlock: 'hexagon',
  ParallelogramBlock: 'parallelogram',
  CylinderBlock: 'cylinder',
  AutoGrowTextBlock: 'text',
  StickiesStickyNoteBlock: 'note',
};

/** The first real text area. Lucid keeps a Placeholder beside it. */
function realText(areas) {
  const list = Array.isArray(areas) ? areas : [];
  return list.find((a) => a.key !== 'Placeholder') || {};
}

/**
 * One Lucid node → one shape.
 *
 * Exported on its own so the importer can be tested a node at a time,
 * which is how the awkward cases — a text block with no fill, an octagon
 * carrying an interest, a link in blue underline — actually get covered.
 */
export function shapeFromLucidNode(node) {
  const props = node?.properties || {};
  const box = parseBoundingBox(props.BoundingBox);
  const area = realText(props.TextAreas);
  const shapeType = BLOCK_SHAPE[props.BlockClass]
    || (String(node?.shapeType || '').toLowerCase() === 'text' ? 'text' : 'rectangle');
  return makeShape({
    id: node.id,
    shapeType,
    ...box,
    rotation: Number(props.Rotation) || 0,
    // A text block in these charts is an annotation floating over the
    // diagram, so it imports with no fill and no border however Lucid
    // recorded the ones it never draws.
    fill: shapeType === 'text' ? 'transparent' : fromLucidColor(props.FillColor, '#ffffff'),
    stroke: shapeType === 'text' ? 'transparent' : fromLucidColor(props.LineColor, '#3a414a'),
    strokeWidth: Number(props.LineWidth) || 2,
    strokeStyle: String(props.StrokeStyle || 'solid'),
    text: area.text || node.label || '',
    textColor: fromLucidColor(area.textColor, '#000000'),
    fontSize: Number(area.fontSize) || 10,
    bold: String(area.bold) === 'true',
    italic: String(area.italic) === 'true',
    underline: String(area.underline) === 'true',
    align: String(props.TextAlign || 'center'),
    valign: String(props.TextVAlign || 'middle'),
    z: Number(props.ZOrder) || 0,
    groupId: node.diagramGroupId || null,
    origin: 'lucid',
  });
}

/** "x: 5157.4, y: -3060, style: Arrow, connectedBlockId: p-Ni" → endpoint. */
export function parseEndpoint(text) {
  const raw = String(text || '');
  const num = (key) => {
    const m = new RegExp(`${key}\\s*:\\s*(-?[\\d.]+)`).exec(raw);
    return m ? Number(m[1]) : null;
  };
  const str = (key) => {
    const m = new RegExp(`${key}\\s*:\\s*([^,]+)`).exec(raw);
    return m ? m[1].trim() : '';
  };
  const shapeId = str('connectedBlockId');
  return {
    shapeId: shapeId || null,
    x: num('x'),
    y: num('y'),
    cap: /style\s*:\s*Arrow/i.test(raw) ? 'arrow' : 'none',
  };
}

export function edgeFromLucidEdge(edge) {
  const props = edge?.properties || {};
  const one = parseEndpoint(props.Endpoint1);
  const two = parseEndpoint(props.Endpoint2);
  const area = realText(props.TextAreas);
  return makeEdge({
    id: edge.id,
    from: { shapeId: edge.sourceId || one.shapeId, x: one.x, y: one.y },
    to: { shapeId: edge.targetId || two.shapeId, x: two.x, y: two.y },
    lineShape: String(props.LineShape || 'elbow').toLowerCase() === 'straight' ? 'straight' : 'elbow',
    rounding: Number(props.Rounding) || 0,
    stroke: fromLucidColor(props.LineColor, '#3a414a'),
    strokeWidth: Number(props.LineWidth) || 2,
    strokeStyle: String(props.StrokeStyle || 'solid'),
    startCap: one.cap,
    endCap: two.cap,
    label: area.text || edge.label || '',
    labelColor: fromLucidColor(area.textColor, '#000000'),
    fontSize: Number(area.fontSize) || 8,
    bold: String(area.bold) === 'true',
    groupId: edge.diagramGroupId || null,
    origin: 'lucid',
  });
}

/**
 * A whole Lucid page → a document.
 *
 * The export nests nodes under `flowcharts` and loose shapes under
 * `standaloneClusters`, and a real chart has both: the parties are a
 * flowchart, the examiner's red notes and the Find-a-Grave links are
 * not. Both are shapes here — the distinction is Lucid's filing, not
 * the diagram's.
 */
export function fromLucid(page) {
  const shapes = [];
  const edges = [];

  const takeNode = (node) => { if (node?.id) shapes.push(shapeFromLucidNode(node)); };
  const walkCluster = (entry) => {
    if (!entry) return;
    if (Array.isArray(entry)) { entry.forEach(walkCluster); return; }
    if (Array.isArray(entry.cluster)) { entry.cluster.forEach(walkCluster); return; }
    takeNode(entry);
  };

  for (const chart of page?.flowcharts || []) {
    (chart.nodes || []).forEach(takeNode);
    for (const edge of chart.edges || []) if (edge?.id) edges.push(edgeFromLucidEdge(edge));
  }
  (page?.standaloneClusters || []).forEach(walkCluster);

  const highest = Math.max(0, ...shapes.map((s) => s.z), ...edges.map((e) => e.z));
  return { shapes, edges, nextZ: highest + 1 };
}
